import { useState } from "react";
import Icon from "../../components/ui/Icon";
import SignalBars from "../../components/ui/SignalBars";
import { useAppState } from "../../state/appState";
import { rssiStrength } from "../../constants/gateways";
import { renderStatusPill, nodeIconForType } from "./networkUtils";
import GatewayDetailsOverlay from "./GatewayDetailsOverlay";
import NodeDetailsOverlay from "./NodeDetailsOverlay";

export default function NetworkView({ nodes, status, lastScanAt, onStartDiscovery, onNavigateToGateway, onAddChannels }) {
  const { gatewayConnected, connectedGateway } = useAppState();
  const [networkFilter, setNetworkFilter] = useState("All");
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const scannedGateway = nodes.find((n) => n.category === "Gateway" || n.id === "bt-bridge");
  const gatewayNode = scannedGateway
    ?? (gatewayConnected && connectedGateway
        ? { ...connectedGateway, type: "Bridge", category: "Gateway", status: "OK" }
        : null);

  const regularNodes = nodes.filter((n) => n.category !== "Gateway" && n.id !== "bt-bridge");
  const selectedNode = selectedNodeId && selectedNodeId !== "gateway"
    ? regularNodes.find((n) => n.id === selectedNodeId) ?? null
    : null;

  const filters = ["All", "Chargers", "Monitoring", "Controllers"];
  const filteredNodes = networkFilter === "All"
    ? regularNodes
    : regularNodes.filter((node) => node.category === networkFilter);

  return (
    <div className="relative h-full">
      <div className="absolute inset-0 overflow-y-auto">
        <div className="px-5 pb-4 space-y-4 pt-2">

          {/* ── Pinned gateway card ─────────────────────────────────────── */}
          {gatewayConnected && gatewayNode && (
            <button
              type="button"
              onClick={() => setSelectedNodeId("gateway")}
              className="w-full rounded-3xl border border-emerald-500/30 bg-brand-purple px-4 py-3 text-left flex items-start gap-3 hover:border-emerald-500/60 transition"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-lg shrink-0">
                <Icon name="bluetooth_connected" className="text-emerald-400" />
              </div>
              <div className="flex-1 flex flex-col gap-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-brand-light truncate">{gatewayNode.name}</span>
                  <span className="inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shrink-0">
                    Gateway
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 text-[11px] text-brand-light/70 leading-snug">
                  <span>BT-CAN Bridge</span>
                  <span>{gatewayNode.rssi ?? "—"} dBm</span>
                  <span>HW: {gatewayNode.hw ?? "—"}</span>
                  <span>FW: {gatewayNode.fw ?? "—"}</span>
                </div>
              </div>
              <div className="flex items-center h-11 pt-1 shrink-0">
                <SignalBars strength={rssiStrength(gatewayNode.rssi ?? -80)} />
              </div>
            </button>
          )}

          {/* ── No gateway connected ─────────────────────────────────────── */}
          {!gatewayConnected && status !== "scanning" && (
            <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-brand-navy bg-brand-navy px-4 py-16 text-center mt-4">
              <div className="flex flex-col items-center gap-3 text-brand-light/50">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-purple text-brand-light/70">
                  <Icon name="bluetooth_disabled" className="text-3xl" />
                </span>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-brand-light">No gateway connected</p>
                  <p className="text-sm text-brand-light/50">
                    Connect to a cBerry Bluetooth-CAN gateway to access the network.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onNavigateToGateway}
                className="w-full max-w-xs rounded-xl bg-brand-magenta px-4 py-3 text-sm font-semibold text-white shadow hover:bg-brand-pink transition"
              >
                Connect to Gateway
              </button>
            </div>
          )}

          {/* ── Scanning ─────────────────────────────────────────────────── */}
          {status === "scanning" && (
            <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-brand-navy bg-brand-navy px-4 py-14 text-center">
              <div className="flex flex-col items-center gap-3 text-brand-light/50">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-purple text-brand-light/70">
                  <span className="material-symbols-outlined text-3xl animate-spin" style={{ animationDuration: "1.2s" }}>
                    sync
                  </span>
                </span>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-brand-light">Scanning for devices…</p>
                  <p className="text-sm text-brand-light/50">Listening for cBerry heartbeats on the CAN bus.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Scan CTA ─────────────────────────────────────────────────── */}
          {gatewayConnected && regularNodes.length === 0 && status !== "scanning" && (
            <button
              type="button"
              onClick={onStartDiscovery}
              className="w-full rounded-xl bg-brand-magenta px-4 py-3 text-sm font-semibold text-white shadow hover:bg-brand-pink transition"
            >
              Scan Network
            </button>
          )}

          {/* ── Filter pills ─────────────────────────────────────────────── */}
          {regularNodes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filters.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setNetworkFilter(id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition ${
                    networkFilter === id
                      ? "bg-brand-magenta border-brand-magenta text-white shadow-sm"
                      : "bg-brand-purple border-brand-purple/70 text-brand-light hover:border-brand-light/40"
                  }`}
                >
                  {id}
                </button>
              ))}
            </div>
          )}

          {/* ── Node count ───────────────────────────────────────────────── */}
          {regularNodes.length > 0 && (
            <div className="flex items-center justify-between text-[11px] px-0.5">
              <span className="text-brand-light/70">{filteredNodes.length} nodes found</span>
              <span className="text-brand-light/40">
                Last scan: {lastScanAt ? new Date(lastScanAt).toLocaleTimeString() : "never"}
              </span>
            </div>
          )}

          {/* ── Node cards ───────────────────────────────────────────────── */}
          {regularNodes.length > 0 && (
            <section className="space-y-3">
              {filteredNodes.map((node) => {
                const freeChannels = node.subtype === "EWL-810"
                  ? (node.channels || []).filter((ch) => !ch.ownerDeviceId)
                  : [];
                return (
                  <div key={node.id} className="space-y-1.5">
                    <button
                      type="button"
                      onClick={() => setSelectedNodeId(node.id)}
                      className="w-full rounded-3xl border border-brand-navy bg-brand-purple px-4 py-3 text-left flex items-start gap-3 hover:border-brand-magenta/70 transition"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-navy bg-brand-purple text-lg shrink-0">
                        <Icon
                          name={nodeIconForType(node.type)}
                          className={
                            node.status === "Error" ? "text-red-300"
                            : node.status === "Needs Update" ? "text-amber-300"
                            : "text-brand-magenta"
                          }
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-semibold text-brand-light">{node.name}</span>
                          {renderStatusPill(node.status)}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 text-[11px] text-brand-light/70 leading-snug">
                          <span>Address: {node.address}</span>
                          <span>Type: {node.type}</span>
                          <span>HW: {node.hw}</span>
                          <span>FW: {node.fw}</span>
                        </div>
                      </div>
                    </button>
                    {freeChannels.length > 0 && (
                      <button
                        type="button"
                        onClick={onAddChannels}
                        className="w-full rounded-2xl border border-brand-magenta/40 bg-brand-magenta/10 px-4 py-2.5 text-sm font-semibold text-brand-magenta hover:bg-brand-magenta/20 transition flex items-center justify-center gap-2"
                      >
                        <Icon name="add" className="text-base" />
                        Add channels
                        <span className="text-[11px] font-normal text-brand-magenta/60">
                          ({freeChannels.length} free slot{freeChannels.length !== 1 ? "s" : ""})
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {/* ── Re-scan ──────────────────────────────────────────────────── */}
          {gatewayConnected && regularNodes.length > 0 && status !== "scanning" && (
            <button
              type="button"
              onClick={onStartDiscovery}
              className="w-full rounded-xl border border-brand-navy bg-brand-purple px-4 py-3 text-sm font-semibold text-brand-light hover:border-brand-magenta/50 transition flex items-center justify-center gap-2"
            >
              <Icon name="refresh" className="text-base text-brand-light/60" />
              Re-scan Network
            </button>
          )}

        </div>
      </div>

      <NodeDetailsOverlay
        isOpen={selectedNodeId !== null && selectedNodeId !== "gateway"}
        node={selectedNode}
        onClose={() => setSelectedNodeId(null)}
      />
      <GatewayDetailsOverlay
        isOpen={selectedNodeId === "gateway"}
        gateway={gatewayNode}
        nodeCount={regularNodes.length}
        isScanned={!!scannedGateway}
        onClose={() => setSelectedNodeId(null)}
      />
    </div>
  );
}
